import { create } from 'zustand';
import { User } from '../types';
import { authService } from '../services/auth';

type AuthState = {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  _lastAuthCheck: number;
};

type AuthStore = AuthState & {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<boolean>;
  clearError: () => void;
};

const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  isLoading: true,
  error: null,
  _lastAuthCheck: 0,
  
  clearError: () => {
    set({ error: null });
  },
    login: async (email, password) => {
    set({ isLoading: true, error: null });
    
    try {
      // Use the auth service to sign in with Supabase
      const user = await authService.signIn(email, password);
      
      set({ 
        user, 
        isAuthenticated: true, 
        isAdmin: user.isAdmin,
        isLoading: false,
        error: null
      });

      // After successful login, merge guest cart with user cart
      const { default: useCartStore } = await import('./cartStore');
      useCartStore.getState().mergeGuestCart();
      
    } catch (error) {
      set({
        user: null,
        isAuthenticated: false,
        isAdmin: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'An error occurred'
      });
    }
  },
    logout: async () => {
    set({ isLoading: true, error: null });
    
    try {
      // Clear any auth caches
      authService.clearCache();
      
      // Use the auth service to sign out from Supabase
      await authService.signOut();
      
      // Don't clear the cart when logging out - preserve it in localStorage
      // The cart should remain available for guest users
      
      set({
        user: null,
        isAuthenticated: false,
        isAdmin: false,
        isLoading: false,
        error: null
      });
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'An error occurred while logging out'
      });
    }
  },
    checkAuth: async () => {
    // Avoid checking auth too frequently (debounce)
    const now = Date.now();
    const state = useAuthStore.getState();
    if (now - state._lastAuthCheck < 1000) {
      return;
    }

    set({ _lastAuthCheck: now, isLoading: true, error: null });
    
    try {
      // Use the auth service to check if the user is already authenticated
      const user = await authService.getCurrentUser();
      
      if (user) {
        set({
          user,
          isAuthenticated: true,
          isAdmin: user.isAdmin,
          isLoading: false,
          error: null
        });

        // Load persisted cart when checking auth on app startup
        const { default: useCartStore } = await import('./cartStore');
        useCartStore.getState().loadPersistedCart();
      } else {
        set({
          user: null,
          isAuthenticated: false,
          isAdmin: false,
          isLoading: false,
          error: null
        });

        // Load persisted cart for guest users too
        const { default: useCartStore } = await import('./cartStore');
        useCartStore.getState().loadPersistedCart();
      }
    } catch (error) {
      set({
        user: null,
        isAuthenticated: false,
        isAdmin: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Session validation failed'
      });
    }
  },
  
  register: async (email, password, firstName, lastName) => {
    set({ isLoading: true, error: null });
    
    try {
      // Clear any existing caches before registration
      authService.clearCache();
      
      // Use the auth service to sign up with Supabase
      const user = await authService.signUp(email, password, firstName, lastName);
      
      // If email confirmation is pending, don't set the user as authenticated
      if (user.emailConfirmationPending) {
        set({
          user: null,
          isAuthenticated: false,
          isAdmin: false,
          isLoading: false,
          error: null
        });
        return true; // Return true to indicate successful registration with email confirmation pending
      }
        // Only set as authenticated if email confirmation is not required
      set({ 
        user, 
        isAuthenticated: true, 
        isAdmin: user.isAdmin,
        isLoading: false,
        error: null
      });

      // After successful registration, merge guest cart
      const { default: useCartStore } = await import('./cartStore');
      useCartStore.getState().mergeGuestCart();
      
      return false; // Return false to indicate no email confirmation is required
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Registration failed'
      });
      throw error; // Re-throw the error so the component can handle it
    }
  }
}));

export default useAuthStore;